import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { userService } from '../../services/userService';
import { fleetService } from '../../services/fleetService';
import { Upload, User, Save, X } from 'lucide-react';

const UserForm = ({
    initialData = null,
    isEditing = false
}) => {
    const navigate = useNavigate();
    const { user: currentUser } = useAuth();
    const [formData, setFormData] = useState({
        username: initialData?.username || '',
        email: initialData?.email || '',
        phone: initialData?.phone || '',
        user_type: initialData?.user_type || 'dispatcher',
        fleet_id: initialData?.fleet_id || '',
        status: initialData?.status || 'active',
        avatar: null
    });
    const [fleets, setFleets] = useState([]);
    const [avatarPreview, setAvatarPreview] = useState(initialData?.avatar_url || null);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    // 不同用戶類型的權限映射
    const userTypeOptions = currentUser.user_type === 'admin'
        ? [
            { value: 'admin', label: '系統管理員' },
            { value: 'dispatcher', label: '調度員' },
            { value: 'fleet_manager', label: '車隊經理' },
            { value: 'support', label: '技術支援' }
        ]
        : [
            { value: 'dispatcher', label: '調度員' }
        ];

    useEffect(() => {
        // 如果是管理員，加載可用車隊
        if (currentUser.user_type === 'admin') {
            fetchFleets();
        } else {
            // 非管理員，直接設置為當前用戶的車隊
            setFormData(prev => ({
                ...prev,
                fleet_id: currentUser.fleet_id
            }));
        }
    }, []);

    const fetchFleets = async () => {
        try {
            const fleetsData = await fleetService.getFleets();
            setFleets(fleetsData);
        } catch (error) {
            console.error('Error fetching fleets:', error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({
                ...prev,
                avatar: file
            }));

            // 預覽圖片
            const reader = new FileReader();
            reader.onloadend = () => {
                setAvatarPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const validateForm = () => {
        const newErrors = {};

        // 用戶名驗證
        if (!formData.username.trim()) {
            newErrors.username = '用戶名不能為空';
        }

        // 郵箱驗證
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!formData.email.trim()) {
            newErrors.email = '電子郵件不能為空';
        } else if (!emailRegex.test(formData.email)) {
            newErrors.email = '請輸入有效的電子郵件';
        }

        // 手機號碼驗證（可選）
        if (formData.phone && !/^[0-9]{10}$/.test(formData.phone)) {
            newErrors.phone = '請輸入有效的手機號碼';
        }

        // 車隊驗證（如果是管理員或需要選擇車隊）
        if (currentUser.user_type === 'admin' && !formData.fleet_id) {
            newErrors.fleet_id = '請選擇所屬車隊';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // 表單驗證
        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);

        try {
            // 創建 FormData 以支持文件上傳
            const submitData = new FormData();
            Object.keys(formData).forEach(key => {
                if (formData[key] !== null && formData[key] !== undefined) {
                    submitData.append(key, formData[key]);
                }
            });

            if (isEditing) {
                // 更新用戶
                await userService.updateUser(initialData.user_id, submitData);
            } else {
                // 創建新用戶
                await userService.createUser(submitData);
            }

            // 導航回用戶列表
            navigate('/users', {
                state: {
                    message: isEditing
                        ? '用戶資訊已成功更新'
                        : '新用戶已成功創建',
                    type: 'success'
                }
            });
        } catch (error) {
            console.error('Error saving user:', error);
            // 可以添加更具體的錯誤處理邏輯
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            {/* 頭像上傳 */}
            <div className="flex items-center space-x-6">
                <div className="w-24 h-24 bg-gray-200 rounded-full relative">
                    {avatarPreview ? (
                        <img
                            src={avatarPreview}
                            alt="Avatar"
                            className="w-full h-full object-cover rounded-full"
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-500">
                            <User className="w-12 h-12" />
                        </div>
                    )}
                    <label
                        htmlFor="avatar"
                        className="absolute bottom-0 right-0 bg-blue-500 text-white p-2 rounded-full cursor-pointer"
                    >
                        <Upload className="w-4 h-4" />
                        <input
                            type="file"
                            id="avatar"
                            accept="image/*"
                            className="hidden"
                            onChange={handleFileChange}
                        />
                    </label>
                </div>
                <div>
                    <p className="text-sm text-gray-600">
                        上傳用戶頭像。支持 JPG、PNG 格式，建議正方形，不超過 2MB。
                    </p>
                </div>
            </div>

            {/* 基本資訊 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        用戶名
                    </label>
                    <input
                        type="text"
                        name="username"
                        value={formData.username}
                        onChange={handleInputChange}
                        className={`w-full border rounded-md p-2 ${errors.username ? 'border-red-500' : 'border-gray-300'
                            }`}
                        placeholder="輸入用戶名"
                    />
                    {errors.username && (
                        <p className="text-red-500 text-xs mt-1">{errors.username}</p>
                    )}
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        電子郵件
                    </label>
                    <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`w-full border rounded-md p-2 ${errors.email ? 'border-red-500' : 'border-gray-300'
                            }`}
                        placeholder="輸入電子郵件"
                    />
                    {errors.email && (
                        <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                    )}
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        聯絡電話
                    </label>
                    <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className={`w-full border rounded-md p-2 ${errors.phone ? 'border-red-500' : 'border-gray-300'
                            }`}
                        placeholder="輸入聯絡電話"
                    />
                    {errors.phone && (
                        <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
                    )}
                </div>

                {/* 用戶類型選擇 */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        用戶類型
                    </label>
                    <select
                        name="user_type"
                        value={formData.user_type}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 rounded-md p-2"
                        disabled={!isEditing && currentUser.user_type !== 'admin'}
                    >
                        {userTypeOptions.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>

                {/* 車隊選擇 */}
                {currentUser.user_type === 'admin' && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            所屬車隊
                        </label>
                        <select
                            name="fleet_id"
                            value={formData.fleet_id}
                            onChange={handleInputChange}
                            className={`w-full border rounded-md p-2 ${errors.fleet_id ? 'border-red-500' : 'border-gray-300'
                                }`}
                        >
                            <option value="">選擇所屬車隊</option>
                            {fleets.map(fleet => (
                                <option key={fleet.fleet_id} value={fleet.fleet_id}>
                                    {fleet.fleet_name}
                                </option>
                            ))}
                        </select>
                        {errors.fleet_id && (
                            <p className="text-red-500 text-xs mt-1">{errors.fleet_id}</p>
                        )}
                    </div>
                )}

                {/* 帳戶狀態 */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        帳戶狀態
                    </label>
                    <select
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 rounded-md p-2"
                        disabled={currentUser.user_type !== 'admin'}
                    >
                        <option value="active">啟用</option>
                        <option value="inactive">停用</option>
                        <option value="pending">待審核</option>
                        <option value="suspended">暫停</option>
                    </select>
                </div>
            </div>

            {/* 提交按鈕 */}
            <div className="flex justify-end space-x-3 mt-6">
                <button
                    type="button"
                    onClick={() => navigate('/users')}
                    className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center"
                >
                    <X className="w-5 h-5 mr-2" />
                    取消
                </button>
                <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center disabled:bg-blue-300"
                >
                    <Save className="w-5 h-5 mr-2" />
                    {isEditing ? '更新用戶' : '創建用戶'}
                </button>
            </div>
        </form>
    );
};

export default UserForm;